import { useMemo } from 'react';

import { Box, BoxProps, Divider } from '@mui/material';
import { CheckboxGroupOptions, CheckboxGroupVariants, type Option } from '@sage/ui-components';

import CareZoneOptionLabel from './CareZoneOptionLabel';
import LoadingState from './LoadingState';
import useCareZones from 'src/hooks/useCareZones';
import useFacilityStaff from 'src/hooks/useFacilityStaff';
import { CareZone } from 'src/store/sageAdminApi';
import { getCareZoneGroups } from 'src/utils/careZoneUtils';

interface CareZoneSelectionProps {
    containerProps?: BoxProps;
    isDisabled?: boolean;
    maxHeight?: number;
    selectedIds?: string[];
    setSelectedIds?: (ids: string[]) => void;
    shouldPoll?: boolean;
}

export default function CareZoneSelection(props: CareZoneSelectionProps) {
    const {
        containerProps = {},
        isDisabled = false,
        maxHeight = 'unset',
        selectedIds = [],
        setSelectedIds = () => {},
        shouldPoll = false,
    } = props;

    const { careZonesWithUnits, isLoading } = useCareZones();

    const { onCallStaffByCareZoneRid, isFetching: isFetchingStaff } = useFacilityStaff(true, {
        pollingInterval: shouldPoll ? 5000 : 0,
    });

    const { groups: careZoneGroups = [] } = useMemo(
        () => getCareZoneGroups(careZonesWithUnits),
        [careZonesWithUnits]
    );

    const selectedOptionsMap = useMemo(
        () => Object.fromEntries(selectedIds.map((id) => [id, true])),
        [selectedIds]
    );

    const handleSelectOption = (optionId: string, isChecked: boolean) => {
        if (isChecked) {
            if (!selectedIds.includes(optionId)) {
                setSelectedIds([...selectedIds, optionId]);
            }
        } else {
            setSelectedIds(selectedIds.filter((id) => id !== optionId));
        }
    };

    const buildOptionsForGroup = (group: CareZone[]): Option[] =>
        group.map((careZone) => ({
            value: careZone.id!,
            disabled: isDisabled,
            label: (
                <CareZoneOptionLabel
                    name={careZone.name}
                    isLoadingMembersOnShift={isFetchingStaff}
                    membersOnShift={onCallStaffByCareZoneRid?.[careZone.id!]?.length ?? 0}
                />
            ),
        }));

    return (
        <>
            {isLoading && <LoadingState />}
            {!isLoading && (
                <Box {...containerProps} sx={{ overflow: 'scroll', maxHeight, m: -1, p: 1 }}>
                    {careZoneGroups.map((careZoneGroup: CareZone[], groupIndex) => (
                        <div key={`group-${groupIndex}`}>
                            {groupIndex > 0 && (
                                <Divider
                                    component="div"
                                    role="presentation"
                                    sx={{ my: 2.5, width: '100%' }}
                                />
                            )}
                            <CheckboxGroupOptions
                                variant={CheckboxGroupVariants.Outlined}
                                options={buildOptionsForGroup(careZoneGroup)}
                                selectedOptions={selectedOptionsMap}
                                onSelectOption={handleSelectOption}
                                formGroupProps={{ 'aria-label': 'Care Zones' }}
                            />
                        </div>
                    ))}
                </Box>
            )}
        </>
    );
}
